import React, { useEffect, useMemo, useState } from 'react';
import trainingApi from '../api';
import { sortByCompletion } from './sortRows';
import { nativeLabelFor } from '../i18n/strings';

const TrainingDetailView = () => {
  const [trainings, setTrainings] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const list = await trainingApi.adminListAll();
        setTrainings(list);
        if (list.length > 0) setSelectedId(String(list[0].id));
      } catch (e) {
        setError(e.message);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await trainingApi.adminTrainingDetail(selectedId);
        if (active) setRows(data);
      } catch (e) {
        if (active) setError(e.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [selectedId]);

  const sortedRows = useMemo(() => sortByCompletion(rows), [rows]);

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-6">By Training</h1>

      <div className="mb-4 flex items-center gap-2">
        <label className="text-sm text-slate-300">Training:</label>
        <select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          className="px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100"
        >
          {trainings.map((t) => (
            <option key={t.id} value={t.id}>{t.title}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-rose-900/40 border border-rose-700 text-rose-200">{error}</div>
      )}

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : (
        <div className="overflow-auto rounded-lg border border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-800 text-slate-300">
              <tr>
                <th className="text-left px-3 py-2">User</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Attempts</th>
                <th className="text-left px-3 py-2">Best Score</th>
                <th className="text-left px-3 py-2">Taken in</th>
                <th className="text-left px-3 py-2">Last Activity</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r) => (
                <tr key={r.user_id} className="border-t border-slate-700">
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.full_name || r.username}</div>
                    <div className="text-xs text-slate-400">{r.username}</div>
                  </td>
                  <td className="px-3 py-2">
                    {r.ever_passed ? (
                      <span className="text-emerald-300">Passed</span>
                    ) : r.attempts > 0 ? (
                      <span className="text-rose-300">Failed</span>
                    ) : (
                      <span className="text-slate-400">Not started</span>
                    )}
                  </td>
                  <td className="px-3 py-2">{r.attempts}</td>
                  <td className="px-3 py-2">
                    {r.best_score !== null && r.best_score !== undefined
                      ? `${Number(r.best_score).toFixed(1)}%`
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-300">
                    {r.languages_used
                      ? r.languages_used.split(',').map((c) => nativeLabelFor(c)).join(', ')
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-400">
                    {r.last_completed_at
                      ? new Date(r.last_completed_at).toLocaleString()
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TrainingDetailView;
